import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Form } from '@unform/web';
import { parse, format, setSeconds } from 'date-fns';
import { isEmpty, isArray } from 'lodash';
import * as Yup from 'yup';
import { Spinner } from '~/components';
import {
  Input,
  DateTime,
  Select,
  Hidden,
  Autocomplete,
  ComboboxMultiple,
} from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import api from '~/services/api';
import { Container } from './styles';
import { MODULO_CUSTOMIZACAO_RUMO } from '~/constants/modules';

export default function FimPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  finalizaGravacao,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [initials, setInitials] = React.useState({});
  const [idatendimento, setIdatendimento] = React.useState(null);
  const [co, setCo] = React.useState(null);
  const [idposto, setIdposto] = React.useState(null);
  const [atendeu, setAtendeu] = React.useState('S');
  const modules = useSelector((state) => state.user.modules);
  const clienteRumo = modules.includes(MODULO_CUSTOMIZACAO_RUMO);

  const optionsAtendimentoRumo = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
    ],
    []
  );

  const optionsAtendimentoGeral = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
      { value: 'X', label: t('s.geral.opcoes.na') },
    ],
    []
  );

  const cellsUser = useMemo(
    () => [
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.cadastro.usuario.nomeusuario'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const optionsAtendimentoPainel = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
      { value: 'X', label: t('s.geral.opcoes.na') },
    ],
    []
  );

  const options = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
    ],
    []
  );

  const formatedDate = (timestamp) => {
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    const date =
      timestamp === null || typeof timestamp === 'undefined'
        ? new Date()
        : parse(timestamp, 'yyyy-MM-dd HH:mm:ss.S', new Date());
    const dateWithZeroSeconds = setSeconds(date, 0);
    return format(dateWithZeroSeconds, 'dd/MM/yyyy HH:mm:ss');
  };

  useEffect(() => {
    async function handleCarregaAtendimento(idatendimento) {
      const { data } = await api.get(`/pitstop/${idatendimento}`);
      setIdatendimento(data.idatendimento);
      setCo(data.codigoatendimento);
      setIdposto(data.idposto);
      return true;
    }
    if (data != null) {
      handleCarregaAtendimento(data.idatendimento);
    }
  }, [data]);

  useEffect(() => {
    async function handleCarregaLimpeza(idatendimento) {
      const { data } = await api.get(`/pitstop/limpeza/${idatendimento}`);
      setInitials(data);
      reference.current.setData(data);
    }
    if (idatendimento) {
      handleCarregaLimpeza(idatendimento);
    }
  }, [idatendimento]);

  async function handleSubmit(param) {
    try {
      const schemaS = Yup.object().shape({
        atendeuatendimentolimpeza: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        datainicioatendimentolimpeza: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        datafimatendimentolimpeza: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        vidroatendimentolimpeza: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        pisoatendimentolimpeza: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        painelatendimentolimpeza: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        banheiroatendimentolimpeza: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        plataformaatendimentolimpeza: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        observacaolimpeza: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schemaN = Yup.object().shape({
        atendeuatendimentolimpeza: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        justificativas: Yup.array().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        observacaolimpeza: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schemaX = Yup.object().shape({
        atendeuatendimentolimpeza: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        observacaolimpeza: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schema = Yup.object().shape({
        atendeuatendimentolimpeza: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
      });
      if (param.atendeuatendimentolimpeza === 'S') {
        await schemaS.validate(param, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentolimpeza === 'N') {
        await schemaN.validate(param, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentolimpeza === 'X') {
        await schemaX.validate(param, {
          abortEarly: false,
        });
      } else {
        await schema.validate(param, {
          abortEarly: false,
        });
      }
      const objSend = {
        ...initials,
        ...param,
        datainicioatendimentolimpeza:
          param.datainicioatendimentolimpeza ||
          initials.datainicioatendimentolimpeza,
        datafimatendimentolimpeza:
          param.datafimatendimentolimpeza || initials.datafimatendimentolimpeza,
        vidroatendimentolimpeza:
          param.vidroatendimentolimpeza || initials.vidroatendimentolimpeza,
        pisoatendimentolimpeza:
          param.pisoatendimentolimpeza || initials.pisoatendimentolimpeza,
        painelatendimentolimpeza:
          param.painelatendimentolimpeza || initials.painelatendimentolimpeza,
        banheiroatendimentolimpeza:
          param.banheiroatendimentolimpeza ||
          initials.banheiroatendimentolimpeza,
        plataformaatendimentolimpeza:
          param.plataformaatendimentolimpeza ||
          initials.plataformaatendimentolimpeza,
        idcliente: client,
      };
      if (objSend.idatendimentolimpeza) {
        await api.put('pitstop/limpeza', objSend);
      } else {
        await api.post('pitstop/limpeza', objSend);
      }
      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      await finalizaGravacao();
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      {isEmpty(initials) ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Container>
              <Hidden name="idatendimentolimpeza" />
              <Hidden name="idatendimento" defaultValue={idatendimento} />
              <Hidden name="idposto" defaultValue={idposto} />
              <Input
                name="codigoatendimento"
                label={t('s.cadastro.pitstop.codigoatendimento')}
                disabled={true}
                defaultValue={co}
              />
              <Select
                name="atendeuatendimentolimpeza"
                label={t('s.cadastro.pitstop.atendeuatendimento')}
                options={
                  clienteRumo ? optionsAtendimentoRumo : optionsAtendimentoGeral
                }
                inputRef={inputRef}
                style={{ marginTop: 8 }}
                getValue={(value) => setAtendeu(value)}
              />
              {clienteRumo === false && (
                <div style={{ marginTop: 8, marginBottom: 10 }}>
                  <Autocomplete
                    name="codigousuario"
                    label="nomecodigousuario"
                    url="usuarios"
                    unique="codigousuario"
                    title={t('s.operacao.abastecimento.codigousuario')}
                    disableClearable={true}
                    params={{
                      filterusrposto: idposto,
                      edicao: Boolean(initials?.idatendimentolimpeza),
                    }}
                    style={{ marginTop: 8, marginBottom: 10 }}
                  />
                </div>
              )}
              {atendeu === 'S' && (
                <>
                  <DateTime
                    label={t('s.geral.opcoes.datahorainicial')}
                    name="datainicioatendimentolimpeza"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 8 }}
                    inputRef={inputRef}
                  />
                  <DateTime
                    label={t('s.geral.opcoes.datahorafinal')}
                    name="datafimatendimentolimpeza"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 16 }}
                  />
                  <Select
                    name="vidroatendimentolimpeza"
                    label={t('s.cadastro.pitstop.vidroatendimentolimpeza')}
                    options={options}
                    style={{ marginTop: 12 }}
                  />
                  <Select
                    name="pisoatendimentolimpeza"
                    label={t('s.cadastro.pitstop.pisoatendimentolimpeza')}
                    options={options}
                    style={{ marginTop: 8 }}
                  />
                  <Select
                    name="painelatendimentolimpeza"
                    label={t('s.cadastro.pitstop.painelatendimentolimpeza')}
                    options={optionsAtendimentoPainel}
                    style={{ marginTop: 8 }}
                  />
                  <Select
                    name="banheiroatendimentolimpeza"
                    label={t('s.cadastro.pitstop.banheiroatendimentolimpeza')}
                    options={options}
                    style={{ marginTop: 8 }}
                  />
                  <Select
                    name="plataformaatendimentolimpeza"
                    label={t('s.cadastro.pitstop.plataformaatendimentolimpeza')}
                    options={options}
                    style={{ marginTop: 8 }}
                  />
                </>
              )}
              {atendeu === 'N' && (
                <>
                  <ComboboxMultiple
                    name="justificativas"
                    label="descricaoobservacao"
                    url="pitstop/justificativa"
                    unique="idobservacaoatendimento"
                    title={t(
                      's.relatorio.alteracaomodooperacional.justificativa'
                    )}
                    params={{
                      wherefield: ['tipoatendimento'],
                      typewherefield: ['EQUALS'],
                      valuewherefield: ['4'],
                      pesquisalista: ['true'],
                    }}
                    style={{ marginBottom: 10 }}
                  />
                </>
              )}
              <Input
                name="observacaolimpeza"
                label={t('s.cadastro.pitstop.observacaoatendimento')}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
                style={{ marginTop: 10 }}
              />
            </Container>
          </Form>
        </>
      )}
    </>
  );
}
